import AskEntryCard from "@/components/ask/AskEntryCard";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus, TrendingUp, Users, AlertCircle, PartyPopper, Bell, MessageCircle, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DebtCard from "@/components/DebtCard";
import EmptyState from "@/components/EmptyState";
import RemindSheet from "@/components/RemindSheet";
import { useAuth } from "@/context/AuthContext";
import { fmtNaira, fmtMoneyList } from "@/lib/format";
import api from "@/lib/api";

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mt-6">
      <div className="flex items-center gap-2 mb-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wide">{title}</h2>
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("owed_to_me");
  const [remindDebt, setRemindDebt] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full rounded-3xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-20 w-full rounded-2xl" />
      </div>
    );
  }

  const d = data || {};
  const iOwe = tab === "i_owe";
  const side = (iOwe ? d.i_owe : d.owed_to_me) || {};
  const heroLabel = iOwe ? "Total you owe" : "Total owed to you";

  return (
    <div className="pb-4">
      <button data-testid="home-greeting-profile" onClick={() => navigate("/profile")} className="group -ml-1 inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-secondary/70 active:scale-[0.98]">
        <span data-testid="home-greeting-avatar" className="h-8 w-8 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display text-sm font-bold">
          {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
        </span>
        <span className="text-sm text-muted-foreground">Hi <span className="font-semibold text-foreground">{user?.name}</span> 👋</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </button>

      {/* Two-sided tabs */}
      <div className="mt-3 grid grid-cols-2 gap-2 rounded-2xl bg-secondary p-1">
        <button
          data-testid="home-tab-owed-to-me"
          onClick={() => setTab("owed_to_me")}
          className={`h-10 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${!iOwe ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
        >
          <ArrowDownLeft className="h-4 w-4" /> Owed to me
        </button>
        <button
          data-testid="home-tab-i-owe"
          onClick={() => setTab("i_owe")}
          className={`h-10 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${iOwe ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
        >
          <ArrowUpRight className="h-4 w-4" /> I owe
        </button>
      </div>

      {/* Hero total */}
      <div className={`mt-3 rounded-3xl p-6 shadow-lg animate-rise ${iOwe ? "bg-foreground text-white shadow-black/10" : "bg-primary text-primary-foreground shadow-primary/20"}`}>
        <p className="text-sm opacity-80">{heroLabel}</p>
        <p data-testid="dashboard-total" className="font-display text-5xl font-extrabold tabular mt-1">
          {fmtNaira((side.by_currency?.[0]?.amount) || 0, side.by_currency?.[0]?.currency)}
        </p>
        {side.by_currency?.length > 1 && (
          <div data-testid="dashboard-total-extra" className="mt-1 flex flex-wrap gap-x-3 text-base font-semibold opacity-90 tabular">
            {side.by_currency.slice(1).map((i) => (
              <span key={i.currency}>+ {fmtNaira(i.amount, i.currency)}</span>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center gap-4 text-sm flex-wrap">
          {side.overdue_by_currency?.length > 0 && (
            <span data-testid="dashboard-overdue" className="inline-flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4 text-red-300" />
              {fmtMoneyList(side.overdue_by_currency)} overdue
            </span>
          )}
          <span data-testid="dashboard-people" className="inline-flex items-center gap-1.5">
            <Users className="h-4 w-4 opacity-80" />
            {iOwe
              ? `You owe ${side.people} ${side.people === 1 ? "person" : "people"}`
              : `${side.people} ${side.people === 1 ? "person owes" : "people owe"} you`}
          </span>
        </div>
      </div>

      {d.plan === "free" && (
        <div className="mt-3 flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3">
          <p className="text-xs text-muted-foreground">
            Free plan · {d.active_count}/{d.free_limit} active
          </p>
          <button onClick={() => navigate("/upgrade")} className="text-xs font-bold text-accent">Upgrade</button>
        </div>
      )}

      <AskEntryCard />

      {!side.has_debts ? (
        <EmptyState
          testId="home-empty-state"
          icon={TrendingUp}
          title={iOwe ? "You don't owe anyone — yet." : "Nobody owes you — yet."}
          subtitle={iOwe
            ? "When you borrow money, record it here so you can keep track and confirm what you've paid back."
            : "When you lend someone money, record it here so you don't have to remember everything yourself."}
        >
          <Button data-testid="home-add-first" onClick={() => navigate("/add")} className="h-12 rounded-xl px-6">
            <Plus className="h-5 w-5" /> {iOwe ? "Add what you owe" : "Add your first debt"}
          </Button>
        </EmptyState>
      ) : (
        <>
          {!iOwe && d.nudge && (
            <div data-testid="home-nudge" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3 animate-rise">
              <div className="h-10 w-10 shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
                <Bell className="h-5 w-5 text-amber-700" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Remind today</p>
                <p className="text-sm text-foreground truncate">{d.nudge.message}</p>
              </div>
              <Button
                data-testid="nudge-remind-button"
                size="sm"
                onClick={() => setRemindDebt(d.nudge.debt)}
                className="shrink-0 h-9 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-white"
              >
                <MessageCircle className="h-4 w-4" /> Remind
              </Button>
            </div>
          )}
          {side.needs_attention?.length > 0 && (
            <Section title={iOwe ? "Overdue to pay" : "Needs attention"} icon={AlertCircle}>
              {side.needs_attention.map((debt) => <DebtCard key={debt.id} debt={debt} />)}
            </Section>
          )}
          {side.due_soon?.length > 0 && (
            <Section title="Due soon">
              {side.due_soon.map((debt) => <DebtCard key={debt.id} debt={debt} />)}
            </Section>
          )}
          <Section title="Recent">
            {side.recent.map((debt) => <DebtCard key={debt.id} debt={debt} />)}
          </Section>
        </>
      )}

      <RemindSheet open={!!remindDebt} onOpenChange={(o) => !o && setRemindDebt(null)} debt={remindDebt} />
    </div>
  );
}
